import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./utils/hooks";
import {
  fetchRootDirectory,
  fetchDirectory,
  removePath,
} from "./api/directorySlice";
import {
  selectFiles,
  selectDirectories,
  selectPath,
  selectLoading,
  selectError,
} from "./api/selectors";
import {
  Path,
  File,
  Directory,
  SearchBar,
  ViewToggle,
  ThemeToggle,
  ContextMenu,
} from "./components";
import { useTheme } from "./contexts/ThemeContext";
import { ViewMode } from "./types/types";
import folderIcon from "./assets/folder_icon.png";
import fileIcon from "./assets/file_icon.png";
import imageIcon from "./assets/image_icon.png";

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg"];
const isImageFile = (name: string) =>
  IMAGE_EXTENSIONS.some((ext) => name.toLowerCase().endsWith(ext));

interface ContextMenuState {
  x: number;
  y: number;
  name: string;
  itemPath: string;
}

interface DisplayItem {
  key: string;
  dragId: string;
  itemType: "directory" | "file";
  name: string;
  id?: string;
  icon: string;
}

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const { directoryId } = useParams<{ directoryId?: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const files = useAppSelector(selectFiles);
  const directories = useAppSelector(selectDirectories);
  const path = useAppSelector(selectPath);
  const loading = useAppSelector(selectLoading);
  const error = useAppSelector(selectError);

  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [dragOrder, setDragOrder] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  const dragItem = useRef<string | null>(null);
  const itemsContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (directoryId) {
      dispatch(fetchDirectory({ directoryId, addToPath: false }));
    } else {
      dispatch(fetchRootDirectory());
    }
  }, [dispatch, directoryId]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const changeDirectory = useCallback(
    (id: string) => {
      dispatch(fetchDirectory({ directoryId: id, addToPath: true }));
      navigate(`/dir/${id}`);
      setSelectedIndex(-1);
      setSearchQuery("");
    },
    [dispatch, navigate]
  );

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const baseItems = useMemo<DisplayItem[]>(
    () => [
      ...(directories ?? []).map((d, index) => ({
        key: `directory-${d.id}-${index}`,
        dragId: `directory:${d.id}:${index}`,
        itemType: "directory" as const,
        name: d.name,
        id: d.id,
        icon: folderIcon,
      })),
      ...(files ?? []).map((f, index) => ({
        key: `file-${index}-${f.name}`,
        dragId: `file:${index}:${f.name}`,
        itemType: "file" as const,
        name: f.name,
        icon: isImageFile(f.name) ? imageIcon : fileIcon,
      })),
    ],
    [directories, files]
  );

  useEffect(() => {
    setDragOrder(baseItems.map((item) => item.dragId));
    setSelectedIndex(-1);
  }, [baseItems]);

  const orderedItems = useMemo(() => {
    if (dragOrder.length === 0) return baseItems;

    const orderMap = new Map(dragOrder.map((id, index) => [id, index]));
    return [...baseItems].sort((a, b) => {
      const ai = orderMap.get(a.dragId);
      const bi = orderMap.get(b.dragId);

      if (ai === undefined && bi === undefined) return 0;
      if (ai === undefined) return 1;
      if (bi === undefined) return -1;
      return ai - bi;
    });
  }, [baseItems, dragOrder]);

  const visibleItems = useMemo(
    () =>
      normalizedSearchQuery
        ? orderedItems.filter((item) =>
            item.name.toLowerCase().includes(normalizedSearchQuery)
          )
        : orderedItems,
    [normalizedSearchQuery, orderedItems]
  );
  const showNoResults =
    !loading && normalizedSearchQuery.length > 0 && visibleItems.length === 0;

  const getGridColumns = useCallback(() => {
    if (viewMode !== "grid") return 1;

    const container = itemsContainerRef.current;
    if (!container) return 1;

    const firstItem = container.querySelector<HTMLElement>("[draggable='true']");
    if (!firstItem) return 1;

    const styles = window.getComputedStyle(firstItem);
    const marginLeft = Number.parseFloat(styles.marginLeft) || 0;
    const marginRight = Number.parseFloat(styles.marginRight) || 0;
    const itemWidth = firstItem.offsetWidth + marginLeft + marginRight;

    if (itemWidth <= 0) return 1;
    return Math.max(1, Math.floor(container.clientWidth / itemWidth));
  }, [viewMode]);

  useEffect(() => {
    setSelectedIndex((prev) => {
      if (visibleItems.length === 0) return -1;
      if (prev < 0) return prev;
      return Math.min(prev, visibleItems.length - 1);
    });
  }, [visibleItems.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "INPUT") return;

      const moveSelection = (delta: number) => {
        setSelectedIndex((prev) => {
          const baseIndex = prev < 0 ? 0 : prev;
          return Math.max(0, Math.min(baseIndex + delta, visibleItems.length - 1));
        });
      };

      if (
        e.key === "ArrowDown" ||
        e.key === "ArrowUp" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight"
      ) {
        e.preventDefault();
        if (visibleItems.length === 0) return;

        if (viewMode === "grid") {
          const columns = getGridColumns();
          if (e.key === "ArrowDown") moveSelection(columns);
          if (e.key === "ArrowUp") moveSelection(-columns);
          if (e.key === "ArrowRight") moveSelection(1);
          if (e.key === "ArrowLeft") moveSelection(-1);
        } else {
          if (e.key === "ArrowDown" || e.key === "ArrowRight") moveSelection(1);
          if (e.key === "ArrowUp" || e.key === "ArrowLeft") moveSelection(-1);
        }
      } else if (e.key === "Enter" && selectedIndex >= 0) {
        const item = visibleItems[selectedIndex];
        if (item?.itemType === "directory" && item.id) {
          changeDirectory(item.id);
        }
      } else if (e.key === "Backspace" && path.length > 1) {
        const newIndex = path.length - 2;
        dispatch(removePath(newIndex));
        const parentId = path[newIndex].id;
        dispatch(fetchDirectory({ directoryId: parentId, addToPath: false }));
        if (newIndex === 0) {
          navigate("/");
        } else {
          navigate(`/dir/${parentId}`);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    visibleItems,
    selectedIndex,
    changeDirectory,
    path,
    dispatch,
    navigate,
    viewMode,
    getGridColumns,
  ]);

  const buildItemPath = (name: string) => {
    const pathNames = path.map((p) => p.name ?? "").join("/");
    return `${pathNames}/${name}`;
  };

  const handleContextMenu = (
    e: React.MouseEvent,
    name: string
  ) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, name, itemPath: buildItemPath(name) });
  };

  // Drag and drop
  const handleDragStart = (id: string, e: React.DragEvent) => {
    dragItem.current = id;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (targetId: string, e: React.DragEvent) => {
    e.preventDefault();
    const sourceId = dragItem.current ?? e.dataTransfer.getData("text/plain");
    if (!sourceId || sourceId === targetId) return;

    setDragOrder((currentOrder) => {
      const ids =
        currentOrder.length > 0
          ? [...currentOrder]
          : baseItems.map((item) => item.dragId);
      const fromIdx = ids.indexOf(sourceId);
      const toIdx = ids.indexOf(targetId);
      if (fromIdx === -1 || toIdx === -1) return ids;

      ids.splice(fromIdx, 1);
      ids.splice(toIdx, 0, sourceId);
      return ids;
    });

    dragItem.current = null;
  };

  const handleDragEnd = () => {
    dragItem.current = null;
  };

  const toolbarPadding = isMobile ? "10px 12px" : "12px 46px";
  const sectionPadding = isMobile ? "14px 12px" : "24px 46px";
  const itemsPadding = isMobile ? "10px 6px" : "12px 34px";

  return (
    <div
      style={{ backgroundColor: theme.background, minHeight: "100vh" }}
      onClick={() => setContextMenu(null)}
    >
      <style>{`
        @keyframes directory-browser-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes directory-browser-pulse {
          0% { opacity: 0.35; }
          50% { opacity: 0.8; }
          100% { opacity: 0.35; }
        }
      `}</style>
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          gap: isMobile ? "10px" : "0",
          padding: toolbarPadding,
          borderBottom: `1px solid ${theme.border}`,
        }}
      >
        <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
          <Path />
        </div>
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            flexWrap: "wrap",
            width: isMobile ? "100%" : "auto",
          }}
        >
          <div style={{ flex: isMobile ? "1 1 100%" : "0 0 auto" }}>
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              theme={theme}
              fullWidth={isMobile}
            />
          </div>
          <ViewToggle viewMode={viewMode} onToggle={setViewMode} theme={theme} />
          <ThemeToggle theme={theme} />
        </div>
      </div>

      {/* Status messages */}
      {loading && (
        <div style={{ padding: sectionPadding }}>
          <div
            style={{
              backgroundColor: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: "12px",
              padding: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "14px",
                color: theme.text,
                fontWeight: 600,
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: "16px",
                  height: "16px",
                  borderRadius: "50%",
                  border: `2px solid ${theme.border}`,
                  borderTopColor: theme.accent,
                  animation: "directory-browser-spin 0.9s linear infinite",
                }}
              />
              Loading directory...
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))",
                gap: "12px",
              }}
            >
              {Array.from({ length: 8 }).map((_, index) => (
                <div
                  key={`loader-item-${index}`}
                  style={{
                    backgroundColor: theme.background,
                    border: `1px solid ${theme.border}`,
                    borderRadius: "8px",
                    padding: "10px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "8px",
                      backgroundColor: theme.hover,
                      animation: `directory-browser-pulse 1.2s ease-in-out ${
                        index * 0.06
                      }s infinite`,
                    }}
                  />
                  <span
                    style={{
                      width: "70%",
                      height: "8px",
                      borderRadius: "999px",
                      backgroundColor: theme.hover,
                      animation: `directory-browser-pulse 1.2s ease-in-out ${
                        index * 0.06
                      }s infinite`,
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {error && (
        <div style={{ color: "#e55", padding: isMobile ? "12px" : "20px 46px" }}>
          Error: {error}
        </div>
      )}

      {/* Items */}
      {!loading && (
        <div
          ref={itemsContainerRef}
          style={{
            display: viewMode === "grid" ? "flex" : "block",
            flexWrap: "wrap",
            padding: viewMode === "grid" ? itemsPadding : isMobile ? "10px 0" : "12px 0",
          }}
        >
          {showNoResults ? (
            <div
              style={{
                margin: isMobile ? "12px 0" : "18px 12px",
                padding: "18px",
                borderRadius: "10px",
                border: `1px solid ${theme.border}`,
                backgroundColor: theme.surface,
                color: theme.text,
                width: isMobile ? "100%" : "auto",
                maxWidth: "520px",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: "6px" }}>
                {`No results for "${searchQuery}"`}
              </div>
              <div style={{ color: theme.textSecondary, fontSize: "13px" }}>
                Try a different phrase or clear the search input.
              </div>
              <button
                type="button"
                style={{
                  marginTop: "12px",
                  padding: "6px 10px",
                  borderRadius: "6px",
                  border: `1px solid ${theme.border}`,
                  backgroundColor: theme.background,
                  color: theme.text,
                  cursor: "pointer",
                }}
                onClick={() => setSearchQuery("")}
              >
                Clear search
              </button>
            </div>
          ) : (
            visibleItems.map((item, idx) =>
              item.itemType === "directory" && item.id ? (
                <Directory
                  key={item.key}
                  name={item.name}
                  id={item.id}
                  icon={item.icon}
                  handleChangeDirectory={changeDirectory}
                  isSelected={selectedIndex === idx}
                  viewMode={viewMode}
                  onContextMenu={(e) => handleContextMenu(e, item.name)}
                  onDragStart={(e) => handleDragStart(item.dragId, e)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(item.dragId, e)}
                  onDragEnd={handleDragEnd}
                />
              ) : (
                <File
                  key={item.key}
                  name={item.name}
                  icon={item.icon}
                  isSelected={selectedIndex === idx}
                  viewMode={viewMode}
                  onContextMenu={(e) => handleContextMenu(e, item.name)}
                  onDragStart={(e) => handleDragStart(item.dragId, e)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(item.dragId, e)}
                  onDragEnd={handleDragEnd}
                />
              )
            )
          )}
        </div>
      )}

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          name={contextMenu.name}
          itemPath={contextMenu.itemPath}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
};

export default App;
